import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  Divider,
  CircularProgress,
  MenuItem,
  Chip,
  InputAdornment,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  AccountBalance as SsnitIcon,
  Calculate as PayeIcon,
  Savings as TierIcon,
  PlaylistAddCheck as AllowanceIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { hrGetSettings, hrSaveSettings } from '../BaseURL';

const PRIMARY    = '#11998e';
const PRIMARY_DK = '#0e8679';
const GRADIENT   = 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)';

const DEFAULT_SETTINGS = {
  currency: 'GHS',
  ssnit_enabled: 1,
  ssnit_employee_rate: 5.5,
  ssnit_employer_rate: 13.0,
  tier2_enabled: 0,
  tier2_employee_rate: 0,
  tier2_employer_rate: 0,
  allowances_taxable: 1,
  allowances_pensionable: 0,
  paye_base: 'gross_minus_ssnit',
  round_net_to: 0.01,
};

const Settings = () => {
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [brackets, setBrackets] = useState([]);

  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userInfo') || '{}')?.name || null; }
    catch { return null; }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(hrGetSettings);
      if (res.data?.success) {
        setSettings({ ...DEFAULT_SETTINGS, ...(res.data.settings || {}) });
        setBrackets((res.data.brackets || []).map(b => ({
          ...b,
          lower_bound: b.lower_bound ?? '',
          upper_bound: b.upper_bound ?? '',
          rate:        b.rate ?? '',
          description: b.description ?? '',
        })));
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to load settings', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleField = (k, v) => setSettings(prev => ({ ...prev, [k]: v }));
  const handleToggle = (k) => (e) => setSettings(prev => ({ ...prev, [k]: e.target.checked ? 1 : 0 }));

  const updateBracket = (idx, field, value) =>
    setBrackets(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));

  const addBracket = () => {
    setBrackets(prev => {
      const nextOrder = prev.length > 0 ? Math.max(...prev.map(b => Number(b.bracket_order) || 0)) + 1 : 1;
      const lastUpper = prev.length > 0 ? prev[prev.length - 1].upper_bound : 0;
      return [...prev, {
        bracket_order: nextOrder,
        lower_bound: lastUpper === '' || lastUpper == null ? '' : Number(lastUpper) + 0.01,
        upper_bound: '',
        rate: '',
        description: '',
      }];
    });
  };

  const removeBracket = (idx) =>
    setBrackets(prev => prev.filter((_, i) => i !== idx).map((b, i) => ({ ...b, bracket_order: i + 1 })));

  const resetGhanaDefaults = async () => {
    const ok = await Swal.fire({
      icon: 'question',
      title: 'Reset to Ghana 2024 defaults?',
      text: 'This replaces all bracket rows in this form. You still need to click Save to persist.',
      showCancelButton: true,
      confirmButtonText: 'Reset',
      confirmButtonColor: PRIMARY,
    });
    if (!ok.isConfirmed) return;
    setBrackets([
      { bracket_order: 1, lower_bound:    0.00, upper_bound:    490.00, rate:  0.00, description: 'Tax-free band' },
      { bracket_order: 2, lower_bound:  490.01, upper_bound:    600.00, rate:  5.00, description: 'First band' },
      { bracket_order: 3, lower_bound:  600.01, upper_bound:    730.00, rate: 10.00, description: 'Second band' },
      { bracket_order: 4, lower_bound:  730.01, upper_bound:   3896.66, rate: 17.50, description: 'Third band' },
      { bracket_order: 5, lower_bound: 3896.67, upper_bound:  19896.66, rate: 25.00, description: 'Fourth band' },
      { bracket_order: 6, lower_bound: 19896.67, upper_bound: 50416.66, rate: 30.00, description: 'Fifth band' },
      { bracket_order: 7, lower_bound: 50416.67, upper_bound:        '', rate: 35.00, description: 'Top band' },
    ]);
  };

  const handleSave = async () => {
    // Sanity check brackets — adjacent rows should be ascending.
    const cleaned = brackets.map((b, i) => ({
      ...b,
      bracket_order: i + 1,
      lower_bound: b.lower_bound === '' ? null : Number(b.lower_bound),
      upper_bound: b.upper_bound === '' ? null : Number(b.upper_bound),
      rate:        Number(b.rate),
    }));

    for (let i = 0; i < cleaned.length; i++) {
      const b = cleaned[i];
      if (b.lower_bound === null || Number.isNaN(b.lower_bound)) {
        return Swal.fire({ icon: 'warning', title: `Row ${i + 1}: lower bound is required` });
      }
      if (Number.isNaN(b.rate)) {
        return Swal.fire({ icon: 'warning', title: `Row ${i + 1}: rate is required` });
      }
      if (b.upper_bound !== null && b.upper_bound <= b.lower_bound) {
        return Swal.fire({ icon: 'warning', title: `Row ${i + 1}: upper bound must exceed lower bound` });
      }
    }

    setSaving(true);
    try {
      await axios.put(hrSaveSettings, {
        settings,
        brackets: cleaned,
        updated_by: currentUser,
      });
      await Swal.fire({ icon: 'success', title: 'Settings saved', timer: 1200, showConfirmButton: false });
      load();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Save failed', text: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)' }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
      p: 2.5,
    }}>
      {/* Header */}
      <Paper elevation={0} sx={{
        mb: 2.5, p: 2,
        borderRadius: 2.5, background: 'white',
        border: '1px solid #c8e6c9',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            background: GRADIENT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(17, 153, 142, 0.35)',
          }}>
            <SettingsIcon sx={{ color: 'white', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 800,
              background: GRADIENT,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              backgroundClip: 'text', lineHeight: 1.1,
            }}>
              Payroll Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mt: 0.25 }}>
              The payroll engine reads these values when computing a run
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            background: GRADIENT, borderRadius: 2,
            textTransform: 'none', fontWeight: 700, px: 3,
            boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
            '&:hover': { background: `linear-gradient(135deg, ${PRIMARY_DK} 0%, #2ed968 100%)` },
            '&.Mui-disabled': { background: '#cbd5e1', color: 'white' },
          }}
        >
          {saving ? 'Saving…' : 'Save All Settings'}
        </Button>
      </Paper>

      {/* Two-column grid: SSNIT, Tier 2, Allowances, PAYE base */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 2, mb: 2.5,
      }}>
        <Section title="SSNIT (Social Security)" icon={SsnitIcon}
          trailing={<Switch checked={!!settings.ssnit_enabled} onChange={handleToggle('ssnit_enabled')} color="success" />}>
          <RateRow
            label="Employee contribution"
            value={settings.ssnit_employee_rate}
            onChange={v => handleField('ssnit_employee_rate', v)}
            disabled={!settings.ssnit_enabled}
            hint="Deducted from the employee's gross"
          />
          <RateRow
            label="Employer contribution"
            value={settings.ssnit_employer_rate}
            onChange={v => handleField('ssnit_employer_rate', v)}
            disabled={!settings.ssnit_enabled}
            hint="Paid by the employer, not deducted from net"
          />
        </Section>

        <Section title="Tier 2 Pension (optional)" icon={TierIcon}
          trailing={<Switch checked={!!settings.tier2_enabled} onChange={handleToggle('tier2_enabled')} color="success" />}>
          <RateRow
            label="Employee contribution"
            value={settings.tier2_employee_rate}
            onChange={v => handleField('tier2_employee_rate', v)}
            disabled={!settings.tier2_enabled}
          />
          <RateRow
            label="Employer contribution"
            value={settings.tier2_employer_rate}
            onChange={v => handleField('tier2_employer_rate', v)}
            disabled={!settings.tier2_enabled}
          />
        </Section>

        <Section title="Allowance Tax Treatment" icon={AllowanceIcon}>
          <ToggleRow
            label="Allowances are taxable"
            checked={!!settings.allowances_taxable}
            onChange={handleToggle('allowances_taxable')}
            hint="When ON, monthly allowances are added to the PAYE taxable base"
          />
          <ToggleRow
            label="Allowances are pensionable"
            checked={!!settings.allowances_pensionable}
            onChange={handleToggle('allowances_pensionable')}
            hint="When ON, allowances are added to the SSNIT contribution base"
          />
        </Section>

        <Section title="PAYE Calculation" icon={PayeIcon}>
          <TextField
            select fullWidth size="small"
            label="PAYE taxable base"
            value={settings.paye_base}
            onChange={(e) => handleField('paye_base', e.target.value)}
            helperText="Ghana standard: gross minus SSNIT employee contribution"
            sx={fieldSx}
          >
            <MenuItem value="gross_minus_ssnit">Gross − SSNIT (Ghana standard)</MenuItem>
            <MenuItem value="gross">Gross (no SSNIT relief)</MenuItem>
          </TextField>
          <TextField
            select fullWidth size="small"
            label="Currency"
            value={settings.currency}
            onChange={(e) => handleField('currency', e.target.value)}
            sx={{ ...fieldSx, mt: 1.5 }}
          >
            {['GHS', 'USD', 'EUR', 'GBP', 'NGN'].map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
        </Section>
      </Box>

      {/* PAYE Brackets */}
      <Paper sx={{
        p: 2, borderRadius: 2.5, background: 'white',
        border: '1px solid #e8f5e9',
        boxShadow: '0 2px 12px rgba(17, 153, 142, 0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 1.5,
              background: GRADIENT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 3px 10px rgba(17, 153, 142, 0.35)',
            }}>
              <PayeIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20', lineHeight: 1.2 }}>
                PAYE Progressive Brackets
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Monthly bands. Leave the last "upper bound" blank for +∞. Rate is a percentage (e.g. 17.5).
              </Typography>
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Reset to Ghana 2024 defaults">
              <Button
                variant="outlined" size="small" startIcon={<RestoreIcon />}
                onClick={resetGhanaDefaults}
                sx={{ textTransform: 'none', borderColor: '#c8e6c9', color: PRIMARY_DK, '&:hover': { borderColor: PRIMARY, bgcolor: '#e8f5e9' } }}
              >
                Reset to GH defaults
              </Button>
            </Tooltip>
            <Button
              variant="contained" size="small" startIcon={<AddIcon />}
              onClick={addBracket}
              sx={{ background: GRADIENT, textTransform: 'none', fontWeight: 600,
                boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
                '&:hover': { background: `linear-gradient(135deg, ${PRIMARY_DK} 0%, #2ed968 100%)` } }}
            >
              Add Bracket
            </Button>
          </Stack>
        </Box>

        <Table size="small" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableHead>
            <TableRow sx={{ background: GRADIENT }}>
              {['#', 'Lower bound', 'Upper bound', 'Rate %', 'Description', ''].map((h, i) => (
                <TableCell key={i} sx={{ color: 'white', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {brackets.map((b, idx) => (
              <TableRow key={idx} sx={{
                bgcolor: idx % 2 === 0 ? 'white' : '#f8fdfb',
                '&:hover': { bgcolor: '#e8f5e9' },
              }}>
                <TableCell sx={{ fontWeight: 700, color: PRIMARY_DK }}>{idx + 1}</TableCell>
                <TableCell>
                  <TextField
                    size="small" type="number" value={b.lower_bound}
                    onChange={(e) => updateBracket(idx, 'lower_bound', e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">{settings.currency}</InputAdornment> }}
                    sx={fieldSx}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" type="number" value={b.upper_bound}
                    onChange={(e) => updateBracket(idx, 'upper_bound', e.target.value)}
                    placeholder="∞"
                    InputProps={{ startAdornment: <InputAdornment position="start">{settings.currency}</InputAdornment> }}
                    sx={fieldSx}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" type="number" value={b.rate}
                    onChange={(e) => updateBracket(idx, 'rate', e.target.value)}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    sx={{ ...fieldSx, width: 110 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" value={b.description}
                    onChange={(e) => updateBracket(idx, 'description', e.target.value)}
                    sx={fieldSx} fullWidth
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => removeBracket(idx)} sx={{ color: '#d32f2f' }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {brackets.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                  <Box sx={{
                    textAlign: 'center', py: 5,
                    background: 'linear-gradient(135deg, #f1fcf9 0%, #e8f5e9 100%)',
                    border: '1px dashed #c8e6c9',
                  }}>
                    <PayeIcon sx={{ fontSize: 44, color: PRIMARY, opacity: 0.5, mb: 1 }} />
                    <Typography variant="subtitle1" sx={{ color: '#1b5e20', fontWeight: 600 }}>
                      No PAYE brackets configured
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click "Add Bracket" or "Reset to GH defaults" to seed the table
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box sx={{
          mt: 2, p: 1.5,
          borderRadius: 2,
          background: 'linear-gradient(135deg, #11998e10 0%, #38ef7d10 100%)',
          border: '1px solid #c8e6c9',
          display: 'flex', alignItems: 'flex-start', gap: 1,
        }}>
          <InfoIcon sx={{ fontSize: 18, color: PRIMARY, mt: '2px' }} />
          <Typography variant="caption" sx={{ color: '#1b5e20', lineHeight: 1.6 }}>
            <strong>How brackets work:</strong> the payroll engine walks the bracket list bottom-up and
            taxes only the portion of the taxable base that falls inside each band. The very last
            row's upper bound should be blank (treated as +∞) so income above the top tier is still taxed.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset':         { borderColor: PRIMARY },
    '&.Mui-focused fieldset':   { borderColor: PRIMARY, borderWidth: 2 },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

const Section = ({ title, icon: Icon, trailing, children }) => (
  <Paper sx={{
    p: 2, borderRadius: 2.5, background: 'white',
    border: '1px solid #e8f5e9',
    boxShadow: '0 2px 12px rgba(17, 153, 142, 0.06)',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          background: GRADIENT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 3px 10px rgba(17, 153, 142, 0.35)',
        }}>
          <Icon sx={{ color: 'white', fontSize: 18 }} />
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1b5e20', lineHeight: 1.2 }}>
          {title}
        </Typography>
      </Box>
      {trailing}
    </Box>
    <Divider sx={{ mb: 1.5, borderColor: '#e8f5e9' }} />
    <Stack spacing={1.5}>{children}</Stack>
  </Paper>
);

const RateRow = ({ label, value, onChange, disabled, hint }) => (
  <Box>
    <TextField
      fullWidth size="small"
      label={label}
      type="number"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
      sx={fieldSx}
      helperText={hint}
      FormHelperTextProps={{ sx: { mx: 0.5, fontSize: '0.68rem' } }}
    />
  </Box>
);

const ToggleRow = ({ label, checked, onChange, hint }) => (
  <Box sx={{
    p: 1.5, borderRadius: 1.5,
    border: '1px solid #e8f5e9',
    bgcolor: checked ? '#f1fcf9' : '#fafafa',
    transition: 'all 0.2s ease',
  }}>
    <FormControlLabel
      control={<Switch checked={checked} onChange={onChange} color="success" />}
      label={
        <Box>
          <Typography sx={{ fontWeight: 600, color: '#1b5e20' }}>{label}</Typography>
          {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
        </Box>
      }
      sx={{ alignItems: 'flex-start', m: 0 }}
    />
  </Box>
);

export default Settings;
